import { HEX, contrastRatio, readableOn, rgba, toRgb } from '@/lib/branding/colour';

/**
 * The emailed ticket page in the organiser's colour, readably.
 *
 * An accent is used three ways and each gets its own colour:
 *
 *   - Fills (the primary button, the medical screening button): the accent
 *     itself, with a label in whichever of near-black or white reads better on
 *     it. Bright yellow gets a dark label, deep green a white one.
 *   - Text in the accent ("Your entry", "Save the QR code", links): the accent
 *     only where it passes WCAG AA (4.5:1) on the page, and on the tint it sits
 *     on. Otherwise the accent pushed towards the ink by the least amount that
 *     passes, and if that would take more than MAX_SHIFT of the way (yellow on
 *     white comes out olive), the ordinary ink instead.
 *   - Tints and borders: the accent at low alpha, decoration only. A border
 *     that would vanish into the page falls back to the hairline.
 *
 * Text has to be worked out per theme, so this returns CSS for a scope class
 * rather than inline style: light on the class, dark under `.dark`.
 */

export const AA = 4.5;
/** Past this the colour stops reading as the brand, so the ink is used. */
export const MAX_SHIFT = 0.45;

/** The v8 grounds and inks (globals.css :root and .dark). */
export const GROUNDS = {
  light: { canvas: '#FFFFFF', ink: '#0C1512', hairline: 'rgba(12,21,18,0.14)' },
  dark: { canvas: '#101614', ink: '#ECF1EF', hairline: 'rgba(255,255,255,0.12)' },
} as const;
export type Ground = keyof typeof GROUNDS;

const hex2 = (n: number) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');

/** Blend `b` into `a` by `t` (0 to 1), as an opaque hex. */
export function mixHex(a: string, b: string, t: number): string {
  const pa = toRgb(a);
  const pb = toRgb(b);
  if (!pa || !pb) return a;
  return `#${pa.map((c, i) => hex2(c + (pb[i] - c) * t)).join('')}`;
}

/** The accent as text on a ground, passing AA on the canvas and on the 12% tint. */
export function accentTextOn(accent: string, ground: Ground): string {
  const { canvas, ink } = GROUNDS[ground];
  const tint = mixHex(canvas, accent, 0.12);
  const passes = (c: string) => contrastRatio(c, canvas) >= AA && contrastRatio(c, tint) >= AA;
  if (passes(accent)) return accent;
  const toward = ground === 'light' ? '#000000' : '#FFFFFF';
  for (let step = 1; step * 0.05 <= MAX_SHIFT + 1e-9; step++) {
    const c = mixHex(accent, toward, step * 0.05);
    if (passes(c)) return c;
  }
  return ink;
}

/** The label on an accent fill: near-black or white, whichever reads better. */
export function labelOn(fill: string): string {
  return readableOn(fill, undefined);
}

export interface SkinTokens {
  vars: Record<string, string>;
}

/** Every token for one ground, or undefined when the accent is not a colour. */
export function organiserSkinTokens(accent: string | null | undefined, ground: Ground): SkinTokens | undefined {
  if (!accent || !HEX.test(accent)) return undefined;
  const g = GROUNDS[ground];
  const on = labelOn(accent);
  const text = accentTextOn(accent, ground);
  // A border must at least be visible. On white a pale accent still shows as
  // a colour (yellow reads as yellow), so only a near-white one gives way; on
  // the dark ground a deep accent all but vanishes, so it needs real contrast.
  const borderVisible = ground === 'light' ? contrastRatio(accent, g.canvas) >= 1.05 : contrastRatio(accent, g.canvas) >= 2;
  const border = borderVisible ? (rgba(accent, 0.45) as string) : g.hairline;
  const vars: Record<string, string> = {
    '--fvd-teal': accent,
    '--fvd-teal-hover': mixHex(accent, on, 0.12),
    '--fvd-teal-on': on,
    '--fvd-teal-solid': accent,
    '--fvd-teal-solid-on': on,
    '--fvd-teal-text': text,
    '--fvd-teal-tint': rgba(accent, 0.12) as string,
    '--fvd-teal-tint-pale': rgba(accent, 0.06) as string,
    '--hub-accent-border': border,
    '--button-text': on,
  };
  return { vars };
}

export const SKIN_CLASS = 'hub-org-skin';

const decls = (o: Record<string, string>) => Object.entries(o).map(([k, v]) => `${k}: ${v};`).join(' ');

/** The CSS for a wrapper with SKIN_CLASS: light on the class, dark under `.dark`. */
export function organiserSkinCss(accent: string | null | undefined): string {
  const light = organiserSkinTokens(accent, 'light');
  const dark = organiserSkinTokens(accent, 'dark');
  if (!light || !dark) return '';
  const s = `.${SKIN_CLASS}`;
  return [`${s} { ${decls(light.vars)} }`, `.dark ${s} { ${decls(dark.vars)} }`].join('\n');
}
